import { num, type ComponentForm } from '../component-types.js';

/**
 * `expressions`: named channels (blink, smile, jawOpen, angry, breathe, ...)
 * that drive blend shapes, bones, child transforms, material properties
 * (flipbook frame for texture-swap eyes), sprite frames and visibility, plus
 * presets, automatic blinking, lip sync and oscillators. Works on any entity
 * (see engine/docs/systems/expressions.md).
 */
const form: ComponentForm = {
  key: 'expressions',
  label: 'Expressions',
  icon: 'data',
  order: 47,
  defaults: {
    channels: {
      blink: { targets: [] },
      jawOpen: { targets: [] },
    },
    blink: { enabled: true },
  },
  fields: [
    {
      key: 'channels',
      label: 'Channels',
      type: { kind: 'json' },
      hint: '{ "blink": { "speed": 0, "targets": [ { "type": "blendShape", "name": "eyeBlink_L" }, { "type": "bone", "name": "lid_L", "rotation": [30, 0, 0] }, { "type": "material", "name": "frame", "entity": "Eyes", "frames": [0, 1, 2] }, { "type": "transform", "entity": "EyelidL", "rotation": [80, 0, 0] }, { "type": "sprite", "frames": [0, 3] }, { "type": "visible", "entity": "EyesClosed" }, { "type": "channel", "name": "squint", "weight": 0.3 } ] } }. Target types: blendShape, bone, transform, material (frame, color, alpha, emissive, emissiveIntensity, roughness, metallic, uvOffset, uvScale, heightScale, normalScale), sprite, visible, channel. Optional "curve": [[in, out], …].',
    },
    { key: 'presets', label: 'Presets', type: { kind: 'json' }, hint: '{ "happy": { "smile": 1, "browUp": 0.3 }, "angry": { "frown": 1 } } — blended by play_expression().' },
    { key: 'expression', label: 'Start preset', type: { kind: 'string', placeholder: 'none' } },
    { key: 'expressionWeight', label: 'Preset strength', type: num(0.05, { min: 0, max: 1 }), read: (v) => v.expressionWeight ?? 1 },
    { key: 'transition', label: 'Preset fade (s)', type: num(0.05, { min: 0 }), read: (v) => v.transition ?? 0.25 },
    { key: 'values', label: 'Fixed values', type: { kind: 'json' }, hint: '{ "smile": 0.2 } — channel values set from the start (scripts override).' },
    { key: 'blendShapes', label: 'Blend shape weights', type: { kind: 'json' }, hint: '{ "mouthOpen": 0.5 } — direct weights of the model\'s shape keys.' },
    {
      key: 'blink',
      label: 'Blinking',
      type: {
        kind: 'object',
        fields: [
          { key: 'enabled', label: 'Auto blink', type: { kind: 'bool' }, read: (v) => v.enabled ?? false },
          { key: 'channel', label: 'Channel', type: { kind: 'string' }, read: (v) => v.channel ?? 'blink' },
          { key: 'interval', label: 'Interval [min, max] s', type: { kind: 'json' }, read: (v) => v.interval ?? [2, 6] },
          { key: 'closeTime', label: 'Close time (s)', type: num(0.01, { min: 0 }), read: (v) => v.closeTime ?? 0.06 },
          { key: 'holdTime', label: 'Hold time (s)', type: num(0.01, { min: 0 }), read: (v) => v.holdTime ?? 0.04 },
          { key: 'doubleChance', label: 'Double blink chance', type: num(0.05, { min: 0, max: 1 }), read: (v) => v.doubleChance ?? 0.15 },
          { key: 'amount', label: 'Amount', type: num(0.05, { min: 0 }), read: (v) => v.amount ?? 1 },
        ],
      },
    },
    {
      key: 'lipSync',
      label: 'Lip sync',
      type: {
        kind: 'object',
        fields: [
          { key: 'enabled', label: 'Enabled', type: { kind: 'bool' }, read: (v) => v.enabled ?? false },
          { key: 'mode', label: 'Mode', type: { kind: 'enum', options: ['audio', 'text', 'procedural'] }, read: (v) => v.mode ?? 'audio', hint: 'audio: loudness of the playing audioSource; text: say() only; procedural: random syllables while talking.' },
          { key: 'source', label: 'Audio entity', type: { kind: 'string', placeholder: 'this / parent' } },
          { key: 'channel', label: 'Mouth channel', type: { kind: 'string' }, read: (v) => v.channel ?? 'jawOpen' },
          { key: 'gain', label: 'Gain', type: num(0.5, { min: 0 }), read: (v) => v.gain ?? 4 },
          { key: 'threshold', label: 'Threshold', type: num(0.005, { min: 0 }), read: (v) => v.threshold ?? 0.02 },
          { key: 'smoothing', label: 'Smoothing (s)', type: num(0.01, { min: 0 }), read: (v) => v.smoothing ?? 0.06 },
          { key: 'rate', label: 'Text rate (letters/s)', type: num(1, { min: 1 }), read: (v) => v.rate ?? 12 },
          { key: 'visemes', label: 'Visemes', type: { kind: 'json' }, hint: '{ "A": "mouthA", "E": "mouthE", "I": "mouthI", "O": "mouthO", "U": "mouthU", "M": "mouthClosed", "F": "mouthF", "L": "mouthL", "S": "mouthS", "rest": "mouthRest" } — viseme → channel.' },
        ],
      },
    },
    {
      key: 'oscillators',
      label: 'Oscillators',
      type: { kind: 'json' },
      hint: '[{ "channel": "breathe", "wave": "sine", "frequency": 0.25, "amplitude": 0.5, "offset": 0.5 }] — waves: sine, triangle, square, saw, noise.',
    },
    { key: 'previewInEditor', label: 'Preview in editor', type: { kind: 'bool' }, read: (v) => v.previewInEditor ?? true },
  ],
};
export default form;
